import React, { Children, useCallback, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/primitives/tabs';
import { FitTextToWidth } from './FitTextToWidth';
import { useList } from '~/util/hooks/useList';
import { NavigateOptions, useNavigate } from '@tanstack/react-router';

interface TabSwitchProps<T extends readonly string[]> {
    options: T;
    optionsDisplay?: { [K in keyof T]: string };
    current: T[number];
    className?: string;
    onChange: (value: T[number]) => void;
    children?: React.ReactNode[] | React.ReactNode;
    size?: 'sm' | 'md' | 'lg';
}

const sizeClassNames = {
    sm: 'clamp-[text-sm-xl-clamp]',
    md: 'clamp-[text-sm-2xl-clamp]',
    lg: 'clamp-[text-sm-3xl-clamp]',
};

function TabSwitch<const T extends readonly string[]>({
    options,
    optionsDisplay,
    current,
    onChange,
    className,
    children,
    size = 'sm',
}: TabSwitchProps<T>) {
    const displayOptions = (optionsDisplay ?? options) as {
        [K in keyof T]: string;
    };
    type Value = T[number];

    const measurementKey = `${options.join('|')}|${(displayOptions as string[]).join('|')}|${size}`;

    const [measurement, setMeasurement] = useState<{
        key: string;
        px: number | null;
    }>({
        key: measurementKey,
        px: null,
    });

    const sharedPx = measurement.key === measurementKey ? measurement.px : null;

    const handleMeasure = useCallback(
        (proposedPx: number) => {
            setMeasurement((prev) => ({
                key: measurementKey,
                px:
                    prev.key !== measurementKey || prev.px == null
                        ? proposedPx
                        : Math.min(prev.px, proposedPx),
            }));
        },
        [measurementKey],
    );

    return (
        <Tabs
            defaultValue={current}
            value={current}
            onValueChange={(value) => onChange(value as Value)}
            className={className}
        >
            <TabsList className="flex justify-center gap-4">
                {options.map((value, index) => (
                    <TabsTrigger
                        key={value}
                        value={value}
                        className={`flex-1 ${sizeClassNames[size]} font-semibold text-center px-4 py-2`}
                    >
                        <FitTextToWidth
                            minPx={11}
                            className="uppercase"
                            groupPx={sharedPx}
                            onMeasure={(px) => handleMeasure(px)}
                        >
                            {displayOptions[index]}
                        </FitTextToWidth>
                    </TabsTrigger>
                ))}
            </TabsList>
            {children &&
                Children.map(children, (child, index) => (
                    <TabsContent value={options[index] as Value}>
                        {child}
                    </TabsContent>
                ))}
        </Tabs>
    );
}

export default TabSwitch;

export const ListTabSwitch = ({
    from,
    resetSearch,
    className,
    size,
    classicDisplay,
    platformerDisplay,
}: {
    from: NavigateOptions['from'];
    resetSearch?: boolean;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
    classicDisplay?: string;
    platformerDisplay?: string;
}) => {
    const list = useList();
    const navigate = useNavigate({ from });
    return (
        <TabSwitch
            className={className}
            options={['classic', 'platformer']}
            optionsDisplay={[
                classicDisplay ?? 'Classic',
                platformerDisplay ?? 'Platformer',
            ]}
            current={list}
            onChange={(list) =>
                navigate({
                    search: (prev) => ({
                        ...(resetSearch ? {} : prev),
                        list,
                    }),
                    replace: true,
                })
            }
            size={size}
        />
    );
};
